import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  Gauge,
  ShieldCheck,
  Wrench,
  X,
} from "lucide-react";
import { listMaintenanceForecasts } from "../api";
import type { MaintenanceForecast, Vehicle } from "../types";

const statusLabel = {
  due: "VENCIDO",
  soon: "PRÓXIMO",
  ok: "EN PLAN",
};

function distanceLabel(value: number) {
  return value < 0
    ? `${Math.abs(value).toLocaleString("es-MX")} km vencidos`
    : `${value.toLocaleString("es-MX")} km restantes`;
}

function daysLabel(value: number) {
  return value < 0 ? `${Math.abs(value)} días vencidos` : `${value} días restantes`;
}

export function PredictiveMaintenancePanel({
  open,
  onClose,
  vehicles,
  onSelectVehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  onSelectVehicle: (vehicle: Vehicle) => void;
}) {
  const [rows, setRows] = useState<MaintenanceForecast[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      setError("");
      listMaintenanceForecasts()
        .then((forecasts) => {
          setRows(forecasts);
          setSelectedId((current) =>
            forecasts.some((forecast) => forecast.id === current)
              ? current
              : forecasts[0]?.id ?? null,
          );
        })
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : "No fue posible cargar los pronósticos"),
        );
    };
    refresh();
    window.addEventListener("gsf:predictive-updated", refresh);
    return () => window.removeEventListener("gsf:predictive-updated", refresh);
  }, [open]);

  const selected = rows.find((row) => row.id === selectedId) || rows[0];
  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === selected?.vehicle_id);
  const summary = useMemo(
    () => ({
      due: rows.filter((row) => row.status === "due").length,
      soon: rows.filter((row) => row.status === "soon").length,
      ok: rows.filter((row) => row.status === "ok").length,
    }),
    [rows],
  );

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        className="admin-panel predictive-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="predictive-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span className="eyebrow">PRONÓSTICO EXPLICABLE · KM + TIEMPO + CONDICIÓN</span>
            <h2 id="predictive-title">Mantenimiento predictivo de flotilla</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar mantenimiento predictivo">
            <X size={18} />
          </button>
        </header>
        <section className="admin-panel__summary">
          <span><AlertTriangle size={17} /><b>{summary.due}</b> vencidos</span>
          <span><CalendarClock size={17} /><b>{summary.soon}</b> próximos</span>
          <span><CheckCircle2 size={17} /><b>{summary.ok}</b> en plan</span>
        </section>
        <div className="predictive-panel__body">
          <section className="predictive-ranking">
            <div className="admin-section-title">
              <span className="eyebrow">PRIORIDAD DE TALLER</span>
              <b>{rows.length} planes activos</b>
            </div>
            {rows.map((row, index) => {
              const vehicle = vehicles.find((item) => item.id === row.vehicle_id);
              return (
                <button
                  type="button"
                  className={`predictive-item predictive-item--${row.status}${selected?.id === row.id ? " is-selected" : ""}`}
                  onClick={() => setSelectedId(row.id)}
                  key={row.id}
                >
                  <b>{String(index + 1).padStart(3, "0")}</b>
                  <span>
                    <small>{vehicle?.unit_code || `UNIDAD ${row.vehicle_id}`} · {statusLabel[row.status]}</small>
                    <strong>{row.title}</strong>
                  </span>
                  <em>{distanceLabel(row.remaining_km)}</em>
                </button>
              );
            })}
            {!rows.length && <p className="maintenance-empty">SIN PLANES PREDICTIVOS</p>}
          </section>
          <section className="predictive-detail">
            {selected && (
              <>
                <div className={`predictive-status predictive-status--${selected.status}`}>
                  <Wrench size={24} />
                  <span><small>ESTADO CALCULADO</small><b>{statusLabel[selected.status]}</b></span>
                </div>
                <div className="predictive-detail__title">
                  <span className="eyebrow">{selectedVehicle?.unit_code} · {selected.component_code}</span>
                  <h3>{selected.title}</h3>
                  <p>{selectedVehicle?.make} {selectedVehicle?.model} · {selectedVehicle?.route_name}</p>
                </div>
                <div className="predictive-deadlines">
                  <article><Gauge size={17} /><span><small>VENTANA POR ODOMETRÍA</small><strong>{distanceLabel(selected.remaining_km)}</strong></span></article>
                  <article><CalendarClock size={17} /><span><small>VENTANA POR CALENDARIO</small><strong>{daysLabel(selected.remaining_days)}</strong></span></article>
                </div>
                <div className="predictive-factors">
                  <span className="eyebrow">POR QUÉ SE CALCULÓ ASÍ</span>
                  {selected.factors_json.map((factor, index) => (
                    <article className={factor.severity ? `is-${factor.severity}` : ""} key={`${factor.code}-${index}`}>
                      <Activity size={14} />
                      <span><strong>{factor.label}</strong><small>{factor.code}</small></span>
                      <b>{factor.severity?.toUpperCase() || "DATO"}</b>
                    </article>
                  ))}
                </div>
                <div className="predictive-recommendation">
                  <ShieldCheck size={18} />
                  <span><small>ACCIÓN RECOMENDADA</small><strong>{selected.recommendation}</strong></span>
                </div>
                <footer>
                  <small>Confianza {selected.confidence.toUpperCase()} · calculado {new Date(selected.calculated_at).toLocaleString("es-MX")}</small>
                  {selectedVehicle && (
                    <button type="button" className="primary-button" onClick={() => { onSelectVehicle(selectedVehicle); onClose(); }}>
                      ABRIR UNIDAD <ArrowRight size={14} />
                    </button>
                  )}
                </footer>
              </>
            )}
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
